import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Icon, Modal, Notification, PRODUCT_PERMISSIONS, productRestriction, RecordsTable, STATUS_TYPES } from "dyl-components";

import sequenceTaskActions from "actions/sequence_tasks";
import sequencesActions from "actions/sequences";
import Header from "./subcomponents/Header";
import Row from "./subcomponents/Row";

import "./index.scss";
import RescheduleSequenceModal from "shared/modals/RescheduleSequenceModal";
import useModal from "utils/useModal";
import NewActivityForm, { ActivityPopup } from "shared/forms/NewActivity";
import SendEmailModal from "shared/modals/SendEmailModal";
import { PhoneUtil } from "utils";
import contactActions from "actions/contact"
import uploadActions from "actions/upload";
import textActions from "actions/text";


const SequenceReports = ({ root = '/settings' }) => {
    const [params] = useSearchParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const emailIntegrations = useSelector(
        (state) => state?.oauth_integrations?.integrations
    );
    const [sequenceTaskBeingRescheduled, setSequenceTaskBeingRescheduled] =
        useState(null);
    const [contactId, setContactId] = useState("");
    const hasEmailIntegrations = emailIntegrations.length > 0;
    const emailModal = useModal();
    const userPhoneCreds = useSelector((state) => state.officeView.creds.extension);
    const isPhoneSystemSupported = PhoneUtil.isPhoneSystemSupported(userPhoneCreds);
    const callActivityModal = useModal();
    const onCallIconClick = (contact_id) => {
        if (!isPhoneSystemSupported) {
            callActivityModal.openModal(contact_id);
        }
    };
    const productPermissions = useSelector(
        (state) => state.subscription.productPermissions
    );
    const fromPhone = useSelector((state) => state?.text?.phones?.[0] || null);

    const hasText = productRestriction(PRODUCT_PERMISSIONS.TEXT_VIEW, productPermissions);

    useEffect(() => {
        dispatch(sequenceTaskActions.read({ ...Object.fromEntries(params), isGetAccountInfo: true }));
        dispatch(
            sequencesActions.readShort({
                deleted: true,
                has_contact: true,
                limit: 2500,
            })
        );
        dispatch(sequencesActions.readUsers());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params]);

    const [filters, setFilters] = useState({
        workflow_id: params.get("workflow_id")?.split(","),
        user_id: params.get("user_id")?.split(","),
        action: params.get("action")?.split(","),
        status: params.get("status")?.split(","),
        filter: params.get("filter"),
    });

    const onFilter = async (_, { name, value }) => {
        setFilters({
            ...filters,
            [name]: value,
        });
    };

    const onSequenceTaskComplete = async (id) => {
        try {
            await dispatch(
                sequenceTaskActions.complete(id, { status: "completed" })
            );
            Notification.alert(
                "Successfully completed sequence task!",
                STATUS_TYPES.SUCCESS
            );
            dispatch(
                sequenceTaskActions.read({ ...Object.fromEntries(params), isGetAccountInfo: true })
            );
            dispatch(
                sequencesActions.readShort({
                    deleted: true,
                    has_contact: true,
                    limit: 2500,
                })
            );
        } catch (e) {
            console.log(e);
            Notification.alert(
                "Failed to complete sequence task",
                STATUS_TYPES.ERROR
            );
        }
    };

    const onCancelSequenceTask = async (id) => {
        try {
            await dispatch(
                sequenceTaskActions.complete(id, { status: "canceled" })
            );
            Notification.alert(
                "Successfully canceled sequence task!",
                STATUS_TYPES.SUCCESS
            );
            dispatch(
                sequenceTaskActions.read({ ...Object.fromEntries(params), isGetAccountInfo: true })
            );
            dispatch(
                sequencesActions.readShort({
                    deleted: true,
                    has_contact: true,
                    limit: 2500,
                })
            );
        } catch (error) {
            console.log(error);
            Notification.alert(
                "Failed to cancel sequence task!",
                STATUS_TYPES.ERROR
            );
        }
    };

    const [emailModalOpenId, setEmailModalOpenId] = useState();

    const getEmailTaskContent = (item, isDisabled) => {
        const canEmail = Boolean(item?.contact?.email);
        if (hasEmailIntegrations && !canEmail) {
            setContactId(item?.contact?.id);
            return (
                <Icon
                    size="large"
                    link
                    className={"fa-solid fa-envelope"}
                    color="primary"
                    style={{ marginTop: 2 }}
                    onClick={emailModal.openModal}
                    disabled={!canEmail || isDisabled}
                />
            );
        } else {
            return (
                <ActivityPopup
                    trigger={
                        <Icon
                            size="large"
                            link
                            className={"fa-solid fa-envelope"}
                            color="primary"
                            style={{ marginTop: 2 }}
                            onClick={() => {
                                setEmailModalOpenId(item?.id);
                            }}
                            disabled={!canEmail || isDisabled}
                        />
                    }
                    activityProps={{
                        readTimeline: () => {},
                        emailOnly: true,
                        contact: item?.contact
                    }}
                    open={emailModalOpenId === item?.id}
                    onOpen={() => setEmailModalOpenId(item?.id)}
                    onClose={() => setEmailModalOpenId(null)}
                    position="bottom right"
                    disabled={!canEmail || isDisabled}
                />
            );
        }
    };

    useEffect(() => {
        const query = new URLSearchParams(params);

        const { workflow_id, user_id, action, status } = filters;
        if (workflow_id?.length > 0) {
            query.set("workflow_id", workflow_id.join(","));
        } else {
            query.delete("workflow_id");
        }
        if (user_id?.length > 0) {
            query.set("user_id", user_id.join(","));
        } else {
            query.delete("user_id");
        }
        if (action?.length > 0) {
            query.set("action", action.join(","));
        } else {
            query.delete("action");
        }
        if (status?.length > 0) {
            query.set("status", status.join(","));
        } else {
            query.delete("status");
        }

        const { filter } = filters;
        if (filter?.length > 0) {
            query.set("filter", filter);
        } else {
            query.delete("filter");
        }

        const query_string = query.toString();
        navigate(
            `${root}/sequence-reports${
                query_string ? `?${query_string}` : ""
            }`
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters]);

    const onPageChange = (_, { activePage }) => {
        const query = new URLSearchParams(params);
        query.set("page", activePage);
        const query_string = query.toString();
        navigate(
            `${root}/sequence-reports${
                query_string ? `?${query_string}` : ""
            }`
        );
    };

    const { sequenceReportsReducer, isReadingSequencesShort } = useSelector(
        (state) => ({
            sequenceReportsReducer: state.sequence_tasks,
            isReadingSequencesShort: state.sequences.isReadingSequencesShort,
        })
    );
    const {
        sequenceTasks = [],
        isReadingSequenceTasks,
        sequence_task_count,
    } = sequenceReportsReducer;

    const splitAttachments = (attachments = []) => {
        return attachments.reduce(
            (acc, item) => {
                if (item instanceof File) {
                    acc.filesToSend.push(item);
                } else {
                    acc.existingAttachments.push(item);
                }
                return acc;
            },
            { existingAttachments: [], filesToSend: [] }
        );
    };
    const buildFinalAttachments = async (
        attachments,
        contactId
    ) => {
        const { existingAttachments, filesToSend } = splitAttachments(attachments);

        let uploadedFiles = [];

        if (filesToSend.length) {
            uploadedFiles = await dispatch(
                uploadActions.uploadFiles(
                    filesToSend,
                    "text",
                    [],
                    contactId,
                    true
                )
            );
        }

        return [
            ...existingAttachments.map(file => ({
                id: file.file_id,
                url: file.url
            })),
            ...uploadedFiles.map(file => ({
                id: file.id,
                url: file.url
            }))
        ];
    };
    const sendTextMessage = async ({
        contactId,
        phone,
        message,
        attachments
    }) => {
        const finalAttachments = await buildFinalAttachments(
            attachments,
            contactId
        );

        await dispatch(
            textActions.sendText({
                from: fromPhone,
                to: phone,
                message,
                contact_id: Number(contactId),
                attachments: finalAttachments
            })
        );
    };

    const onTaskConfirm = async (id) => {
        try {
            const task = sequenceTasks.find((task) => task.id === id);
            const taskData = task?.action_data;
            const contactId = task?.contact?.id;
            const message = taskData?.message;
            const attachments = taskData?.attachments ?? [];

            const contact = await dispatch(contactActions.getContactOptions({ search: contactId }));
            const phone = contact?.data?.[0]?.phone?.phone;

            await sendTextMessage({
                contactId,
                phone,
                message,
                attachments
            });

            await dispatch(
                sequenceTaskActions.complete(id, { status: "completed" })
            );
            dispatch(
                sequenceTaskActions.read({ ...Object.fromEntries(params), isGetAccountInfo: true })
            );

            Notification.alert(
                "Successfully sent text!",
                STATUS_TYPES.SUCCESS,
                true
            );
        } catch (error) {
            console.log("Send Text Error", error);
            Notification.alert(
                "Failed to send text!",
                STATUS_TYPES.ERROR,
                true
            );
        }
    };

    return (
        <div className="SequenceReportsPage">
            <Modal
                open={callActivityModal?.isVisible}
                onClose={callActivityModal?.closeModal}
                size="tiny"
            >
                <Modal.Content>
                    <NewActivityForm
                        onClose={callActivityModal?.closeModal}
                        hideActivitySelection
                        contact={{ id: callActivityModal?.data }}
                        isContact
                    />
                </Modal.Content>
            </Modal>
            <RescheduleSequenceModal
                open={sequenceTaskBeingRescheduled}
                sequenceTaskId={sequenceTaskBeingRescheduled}
                onClose={() => {
                    setSequenceTaskBeingRescheduled(null);
                }}
                refresh={() => {
                    dispatch(
                        sequenceTaskActions.read({
                            ...Object.fromEntries(params),
                            isGetAccountInfo: true
                        })
                    );
                    dispatch(
                        sequencesActions.readShort({
                            deleted: true,
                            has_contact: true,
                            limit: 2500,
                        })
                    );
                }}
            />
            <SendEmailModal
                open={emailModal?.isVisible}
                onClose={emailModal?.closeModal}
                contact_id={contactId}
            />
            <RecordsTable
                tableBodyClassName="SequenceReportsPage__Table"
                isTableLoading={
                    isReadingSequenceTasks || isReadingSequencesShort
                }
                isSearchResultEmpty={false}
                isTableEmpty={!sequence_task_count || sequence_task_count === 0}
                TableHeader={<Header onFilter={onFilter} root={root}/>}
                TableRows={sequenceTasks.map((sequenceTask) => (
                    <Row
                        key={sequenceTask.id}
                        sequenceTask={sequenceTask}
                        onSequenceTaskReschedule={(id) => {
                            setSequenceTaskBeingRescheduled(id);
                        }}
                        onTaskComplete={onSequenceTaskComplete}
                        onTaskRemove={onCancelSequenceTask}
                        getEmailTaskContent={getEmailTaskContent}
                        onCallIconClick={onCallIconClick}
                        hasText={hasText}
                        onTaskConfirm={onTaskConfirm}
                    />
                ))}
                recordsCount={sequence_task_count}
                activePage={params.get("page")}
                onPageChange={onPageChange}
                recordsName="Sequence"
                emptySubtitle={<span></span>}
                emptyListTitle="No Sequences Created"
            />
        </div>
    );
};

export default SequenceReports;
